import type { ReactElement, ReactNode } from "react";
import { ChevronRight, Grid3x3, Link45deg, X } from "react-bootstrap-icons";
import { createPortal } from "react-dom";

interface ShareScoreSheetProps {
    open: boolean;
    enableShareWithLink?: boolean;
    onClose: () => void;
    onShareGridOnly: () => void;
    onShareGridWithLink: () => void;
    onShareReddit: () => void;
}

const REDDIT_ICON = (
    <span
        className="tw-flex tw-items-center tw-justify-center"
        style={{
            width: 18,
            height: 18,
            borderRadius: "50%",
            background: "#ff4500",
            color: "#fff",
            fontSize: 11,
            fontWeight: 700,
        }}
    >
        R
    </span>
);

/**
 * Bottom sheet with three share actions; dismisses on backdrop tap or close button.
 */
export default function ShareScoreSheet({
    open,
    enableShareWithLink = true,
    onClose,
    onShareGridOnly,
    onShareGridWithLink,
    onShareReddit,
}: ShareScoreSheetProps): ReactElement | null {
    if (!open) {
        return null;
    }
    const rows: ReadonlyArray<{
        key: string;
        label: string;
        icon: ReactNode;
        onClick: () => void;
    }> = [
        {
            key: "grid",
            label: "Share grid only",
            icon: <Grid3x3 size={18} color="#f97316" />,
            onClick: onShareGridOnly,
        },
        ...(enableShareWithLink
            ? [
                  {
                      key: "grid-link",
                      label: "Share grid with link",
                      icon: <Link45deg size={18} color="#a78bfa" />,
                      onClick: onShareGridWithLink,
                  },
              ]
            : []),
        {
            key: "reddit",
            label: "Share to Reddit",
            icon: REDDIT_ICON,
            onClick: onShareReddit,
        },
    ];
    return createPortal(<ShareSheetContent onClose={onClose} rows={rows} />, document.body);
}

function ShareSheetContent({
    onClose,
    rows,
}: {
    onClose: () => void;
    rows: ReadonlyArray<{ key: string; label: string; icon: ReactNode; onClick: () => void }>;
}): ReactElement {
    return (
        <div
            role="presentation"
            onClick={onClose}
            className="tw-fixed tw-inset-0 tw-z-[10050] tw-flex tw-items-end tw-justify-center tw-font-noto"
            style={{
                background: "rgba(8, 4, 18, 0.6)",
                animation: "shareFadeIn 180ms ease-out",
            }}
        >
            <style>{`
                @keyframes shareFadeIn { from { opacity: 0 } to { opacity: 1 } }
                @keyframes shareSlideUp { from { transform: translateY(100%) } to { transform: translateY(0) } }
            `}</style>
            <div
                role="dialog"
                aria-modal="true"
                aria-label="Share"
                onClick={(event) => event.stopPropagation()}
                style={{
                    width: "100%",
                    maxWidth: 480,
                    background: "#1a1a2e",
                    borderTopLeftRadius: 20,
                    borderTopRightRadius: 20,
                    padding: "14px 16px 22px",
                    borderTop: "1px solid rgba(255,255,255,0.08)",
                    animation: "shareSlideUp 240ms ease-out",
                }}
            >
                <ShareSheetHeader onClose={onClose} />
                <ShareSheetRows rows={rows} onClose={onClose} />
            </div>
        </div>
    );
}

function ShareSheetHeader({ onClose }: { onClose: () => void }): ReactElement {
    return (
        <div className="tw-flex tw-items-center tw-justify-between" style={{ marginBottom: 10 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: "#fff" }}>Share</span>
            <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "rgba(255,255,255,0.6)",
                    padding: 4,
                    display: "flex",
                }}
            >
                <X size={16} />
            </button>
        </div>
    );
}

function ShareSheetRows({
    rows,
    onClose,
}: {
    rows: ReadonlyArray<{ key: string; label: string; icon: ReactNode; onClick: () => void }>;
    onClose: () => void;
}): ReactElement {
    return (
        <div className="tw-flex tw-flex-col" style={{ gap: 4 }}>
            {rows.map((row) => (
                <button
                    key={row.key}
                    type="button"
                    onClick={() => {
                        row.onClick();
                        onClose();
                    }}
                    className="tw-flex tw-items-center tw-cursor-pointer"
                    style={{
                        width: "100%",
                        background: "#232340",
                        border: "1px solid rgba(255,255,255,0.06)",
                        borderRadius: 12,
                        padding: "12px 14px",
                        gap: 12,
                        color: "#fff",
                    }}
                >
                    <div
                        className="tw-flex tw-items-center tw-justify-center"
                        style={{
                            width: 28,
                            height: 28,
                            borderRadius: 8,
                            background: "rgba(255,255,255,0.05)",
                        }}
                    >
                        {row.icon}
                    </div>
                    <span style={{ flex: 1, textAlign: "left", fontSize: 14, fontWeight: 500 }}>{row.label}</span>
                    <ChevronRight size={16} color="rgba(255,255,255,0.4)" />
                </button>
            ))}
        </div>
    );
}
